import React, { useState } from 'react';
import {
  TextField,
  DateField,
  NumberField,
  FunctionField,
  Identifier,
} from 'react-admin';
import {
  makeStyles,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import { Info } from '@material-ui/icons';
import PanoramaFishEye from '@material-ui/icons/PanoramaFishEye';
import CheckIcon from '@material-ui/icons/Check';
import Button from '@material-ui/core/Button';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { PhaseTypeEnum, JobMasterSheetInterface } from '@vatos-pas/common';
import { SupervisorField } from 'components/SupervisorListItems';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { CAN_CHANGE_DATES_MASTER_SHEET } from '../../../../providers/permissions';
import {
  isPhaseColumnSkipped,
  isStartingPhaseColumn,
  SKIPPED_PHASE_DISPLAY,
} from 'utils/phaseUtils';
import { dateWithoutServerTimezone } from '../../utils/dateWithoutServerTimezone';
import { DateChangeHistoryModal } from '../DateChangeHistoryModal';
import { PhaseContractorField } from '../PhaseContractorField';
import { ColumnDef, EditableDateColumnDef } from '../../config/types';

/**
 * Extends JobMasterSheetInterface with an index signature to allow
 * dynamic field access (e.g. bump1-5 phase fields, currentPhaseDateApproved)
 * driven by column config strings.
 */
type MasterSheetRecord = JobMasterSheetInterface & {
  id: Identifier;
  [key: string]: unknown;
};

/** Read a dynamic string field. Returns the value only if it's actually a string. */
function str(
  record: MasterSheetRecord | undefined,
  key: string,
): string | null | undefined {
  if (!record) return undefined;
  const val = record[key];
  if (typeof val === 'string') return val;
  if (val === null) return null;
  return undefined;
}

/** Read a dynamic boolean field. Returns the value only if it's actually a boolean. */
function bool(
  record: MasterSheetRecord | undefined,
  key: string,
): boolean | undefined {
  if (!record) return undefined;
  const val = record[key];
  return typeof val === 'boolean' ? val : undefined;
}

/** Read a dynamic date-picker value from FieldState. Excludes the `submitted` boolean. */
function dateFieldValue(
  fieldState: FieldState | undefined,
  key: string,
): MaterialUiPickersDate | undefined {
  if (!fieldState) return undefined;
  const val = fieldState[key];
  if (val === undefined || typeof val === 'boolean') return undefined;
  return val;
}

/** Per-row editing state: stores date picker values + submitted flag. */
interface FieldState {
  submitted?: boolean;
  [customDateField: string]: MaterialUiPickersDate | boolean | undefined;
}

/** Map of record id → field editing state. */
export type FieldsMap = Record<string, FieldState>;

/** Curried field change handler signature used by date pickers. */
type FieldChangeHandler = (
  field: string,
) => (
  id: string,
) => (event: { target: { value: MaterialUiPickersDate | boolean } }) => void;

const useColumnStyles = makeStyles({
  lightgray: {
    color: '#646466',
  },
  signedStatusBadge: {
    backgroundColor: '#27AE60',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '10px',
    '& > svg': {
      width: '20px',
      height: '20px',
    },
  },
});

const formatDate = (d: Date): string =>
  `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;

const dashFieldRender =
  (source: string) => (record: MasterSheetRecord | undefined) => {
    const value = record?.[source];
    return value == null || value === '' || value === 0 || value === '0'
      ? '–'
      : value;
  };

const EditableDateCell: React.FC<{
  record: MasterSheetRecord | undefined;
  col: EditableDateColumnDef;
  fields: FieldsMap;
  onChange: FieldChangeHandler;
}> = ({ record, col, fields, onChange }) => {
  const { hasPermission } = PermissionsProvider.useContainer();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);

  const id = record?.id ? String(record.id) : '';

  if (
    isPhaseColumnSkipped(
      record?.currentPhaseType,
      col.priority,
      str(record, col.contractorField),
      str(record, col.suggestedField),
      record?.[col.dateSource],
    )
  ) {
    return <label>{SKIPPED_PHASE_DISPLAY}</label>;
  }

  if (
    isStartingPhaseColumn(
      record?.currentPhaseType,
      col.priority,
      str(record, col.contractorField),
      str(record, col.suggestedField),
      record?.[col.dateSource],
      bool(record, 'currentPhaseApproved'),
    )
  ) {
    const date = str(record, 'currentPhaseDateApproved');
    if (!date) return <label>{SKIPPED_PHASE_DISPLAY}</label>;
    const d = dateWithoutServerTimezone(date);
    return <label>{formatDate(d)}</label>;
  }

  const isSupervisor = hasPermission(CAN_CHANGE_DATES_MASTER_SHEET);

  const dateStr = str(record, col.dateSource);
  let dateValue: MaterialUiPickersDate | Date | null = dateStr
    ? dateWithoutServerTimezone(dateStr)
    : null;

  const fieldDateVal = dateFieldValue(fields[id], col.customDateField);
  if (fieldDateVal !== undefined) {
    dateValue = fieldDateVal;
  }

  const onDateChange = (value: MaterialUiPickersDate) => {
    onChange(col.customDateField)(id)({ target: { value } });
    onChange('submitted')(id)({ target: { value: false } });
  };

  const showHistoryIcon = record?.[col.dateSource] !== null;

  return (
    <>
      <Box
        display="flex"
        alignItems="flex-end"
        minWidth={160}
        onClick={e => e.stopPropagation()}
      >
        <Box width={140}>
          <KeyboardDatePicker
            disableToolbar
            variant="inline"
            format="MM/dd/yyyy"
            margin="normal"
            id={`date-picker-${col.customDateField}-${id}`}
            KeyboardButtonProps={{ 'aria-label': 'Change Date' }}
            onChange={onDateChange}
            defaultValue={dateValue}
            value={dateValue}
            disabled={!isSupervisor}
          />
        </Box>
        {showHistoryIcon && (
          <Tooltip title={`View ${col.historyTitle}`}>
            <IconButton
              size="small"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setHistoryModalOpen(true);
              }}
              style={{ marginLeft: 4, marginBottom: 8 }}
            >
              <Info fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <DateChangeHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={col.historyTitle}
        jobPhaseId={str(record, col.jobPhaseIdField) || ''}
      />
    </>
  );
};

const StatusField: React.FC<{
  record?: MasterSheetRecord;
  label?: string;
}> = props => {
  const classes = useColumnStyles();
  const record = props.record;
  if (!record) return null;

  const jobPhaseType = record.currentPhaseType;
  const jobPhaseContractorId = record.currentPhaseContractorId;
  const jobPhaseDateRequested = record.currentPhaseDateRequested;
  const jobPhaseActive = record.activeJob;

  const Checked = () => (
    <Box className={classes.signedStatusBadge}>
      <CheckIcon />
    </Box>
  );

  if (jobPhaseType === PhaseTypeEnum.Spraying && jobPhaseDateRequested) {
    return (
      <Box display="flex" style={{ gap: '3px' }} alignItems="center">
        <Checked />
        <Typography>|</Typography>
        <Checked />
        <Checked />
        <Checked />
        <Typography>|</Typography>
        <Checked />
      </Box>
    );
  }
  if (jobPhaseType === PhaseTypeEnum.Spraying) {
    return (
      <Box display="flex" style={{ gap: '3px' }} alignItems="center">
        <Checked />
        <Typography>|</Typography>
        <Checked />
        <Checked />
        <Checked />
        <Typography>|</Typography>
        <PanoramaFishEye style={{ marginLeft: '-3px' }} />
      </Box>
    );
  }
  if (jobPhaseType === PhaseTypeEnum.Finishing && jobPhaseContractorId) {
    return (
      <Box display="flex" style={{ gap: '3px' }} alignItems="center">
        <Checked />
        <Typography>|</Typography>
        <Checked />
        <Checked />
        <PanoramaFishEye style={{ marginLeft: '-2px' }} />
      </Box>
    );
  }
  if (jobPhaseType === PhaseTypeEnum.Finishing && jobPhaseDateRequested) {
    return (
      <Box display="flex" style={{ gap: '3px' }} alignItems="center">
        <Checked />
        <Typography>|</Typography>
        <Checked />
        <PanoramaFishEye style={{ marginLeft: '-2px' }} />
      </Box>
    );
  }
  if (jobPhaseType === PhaseTypeEnum.Finishing) {
    return (
      <Box display="flex" style={{ gap: '3px' }} alignItems="center">
        <Checked />
        <Typography>|</Typography>
        <PanoramaFishEye style={{ marginLeft: '-3px' }} />
      </Box>
    );
  }
  if (
    jobPhaseActive ||
    (!jobPhaseActive && jobPhaseType === PhaseTypeEnum.Hanging)
  ) {
    return (
      <Box display="flex" alignItems="center">
        <PanoramaFishEye style={{ marginLeft: '-2px' }} />
      </Box>
    );
  }
  return null;
};

const GenericSubmitButton: React.FC<{
  loading: boolean;
  onSubmit: (record: MasterSheetRecord) => Promise<void>;
  record?: MasterSheetRecord;
  label?: string;
  fields: FieldsMap;
  editableDateColumns: EditableDateColumnDef[];
}> = ({ loading, onSubmit, record, fields, editableDateColumns, ...props }) => {
  const { hasPermission } = PermissionsProvider.useContainer();
  const isSupervisor = hasPermission(CAN_CHANGE_DATES_MASTER_SHEET);

  if (!isSupervisor || !record) return null;

  let disabled = true;
  const changed = fields[String(record.id)];

  if (changed) {
    for (const col of editableDateColumns) {
      const dateVal = dateFieldValue(changed, col.customDateField);
      const originalStr = str(record, col.dateSource);

      if (
        (dateVal === null && originalStr) ||
        (dateVal && !originalStr) ||
        (dateVal &&
          originalStr &&
          formatDate(dateVal.toJSDate()) !==
            formatDate(dateWithoutServerTimezone(originalStr)))
      ) {
        disabled = false;
        break;
      }
    }
  }

  if (loading || (changed && changed.submitted)) {
    disabled = true;
  }

  return (
    <Box onClick={e => e.stopPropagation()}>
      <Button
        variant="contained"
        color="primary"
        {...props}
        onClick={() => onSubmit(record)}
        disabled={disabled}
      >
        Submit
      </Button>
    </Box>
  );
};

export interface ColumnRenderContext {
  fields: FieldsMap;
  onFieldChange: FieldChangeHandler;
  loading: boolean;
  submitDates: (record: MasterSheetRecord) => Promise<void>;
  isWarranty: boolean;
  isSupervisor: boolean;
  isAdmin: boolean;
}

export function renderColumns(
  columns: ColumnDef[],
  ctx: ColumnRenderContext,
): (React.ReactElement | null)[] {
  const editableDateCols = columns.filter(
    (c): c is EditableDateColumnDef => c.type === 'editable-date',
  );

  return columns.map((col, index) => {
    // Warranty/role visibility filtering
    if (col.hideOnWarranty && ctx.isWarranty) return null;
    if (col.warrantyOnly && !ctx.isWarranty) return null;
    if ('adminOnly' in col && col.adminOnly && !ctx.isAdmin) return null;
    // Supervisor field is hidden for supervisors
    if (col.type === 'supervisor' && ctx.isSupervisor) return null;

    const key = `col-${index}`;

    switch (col.type) {
      case 'text':
        return (
          <TextField
            key={key}
            source={col.source}
            label={col.label}
            sortable={col.sortable}
          />
        );

      case 'date':
        return <DateField key={key} source={col.source} label={col.label} />;

      case 'number':
        return <NumberField key={key} source={col.source} label={col.label} />;

      case 'dash-field':
        return (
          <FunctionField<MasterSheetRecord>
            key={key}
            label={col.label}
            render={dashFieldRender(col.source)}
          />
        );

      case 'phase-name':
        return (
          <FunctionField<MasterSheetRecord>
            key={key}
            source={col.source}
            label={col.label}
            render={record => {
              const name = str(record, col.source);
              return <label>{name || ''}</label>;
            }}
          />
        );

      case 'supervisor':
        return <SupervisorField key={key} label={col.label} />;

      case 'status':
        return <StatusField key={key} label={col.label} />;

      case 'phase-contractor': {
        const { priority, contractorField, suggestedField, dateField } = col;
        return (
          <FunctionField<MasterSheetRecord>
            key={key}
            label={col.label}
            sortable={false}
            render={record => {
              const contractor = str(record, contractorField);
              const suggested = str(record, suggestedField);

              if (
                isPhaseColumnSkipped(
                  record?.currentPhaseType,
                  priority,
                  contractor,
                  suggested,
                  record?.[dateField],
                )
              ) {
                return <label>{SKIPPED_PHASE_DISPLAY}</label>;
              }
              if (
                isStartingPhaseColumn(
                  record?.currentPhaseType,
                  priority,
                  contractor,
                  suggested,
                  record?.[dateField],
                  bool(record, 'currentPhaseApproved'),
                )
              ) {
                return <label>{SKIPPED_PHASE_DISPLAY}</label>;
              }
              if (contractor) {
                return <label>{contractor}</label>;
              }
              if (suggested) {
                return <PhaseContractorSuggested name={suggested} />;
              }
              return <label></label>;
            }}
          />
        );
      }

      case 'phase-date': {
        const { source, priority, contractorField, suggestedField } = col;
        return (
          <FunctionField<MasterSheetRecord>
            key={key}
            source={source}
            label={col.label}
            sortable={col.sortable}
            render={record => {
              const contractor = str(record, contractorField);
              const suggested = str(record, suggestedField);

              if (
                isPhaseColumnSkipped(
                  record?.currentPhaseType,
                  priority,
                  contractor,
                  suggested,
                  record?.[source],
                )
              ) {
                return <label>{SKIPPED_PHASE_DISPLAY}</label>;
              }
              if (
                isStartingPhaseColumn(
                  record?.currentPhaseType,
                  priority,
                  contractor,
                  suggested,
                  record?.[source],
                  bool(record, 'currentPhaseApproved'),
                )
              ) {
                const date = str(record, 'currentPhaseDateApproved');
                if (!date) return <label>{SKIPPED_PHASE_DISPLAY}</label>;
                const d = dateWithoutServerTimezone(date);
                return <label>{formatDate(d)}</label>;
              }
              const dateVal = str(record, source);
              if (!dateVal) return <label></label>;
              const d = dateWithoutServerTimezone(dateVal);
              return <label>{formatDate(d)}</label>;
            }}
          />
        );
      }

      case 'editable-date':
        return (
          <FunctionField<MasterSheetRecord>
            key={key}
            source={col.dateSource}
            label={col.label}
            sortable={col.sortable}
            render={record => (
              <EditableDateCell
                record={record}
                col={col}
                fields={ctx.fields}
                onChange={ctx.onFieldChange}
              />
            )}
          />
        );

      case 'phase-contractor-current':
        return <PhaseContractorField key={key} label={col.label} />;

      case 'pre-rock':
        return (
          <FunctionField<MasterSheetRecord>
            key={key}
            source="preRock"
            label={col.label}
            render={record => (
              <label>{record?.preRock === true ? 'Yes' : 'No'}</label>
            )}
          />
        );

      case 'submit-button':
        return (
          <GenericSubmitButton
            key={key}
            label={col.label}
            loading={ctx.loading}
            fields={ctx.fields}
            onSubmit={ctx.submitDates}
            editableDateColumns={editableDateCols}
          />
        );

      default:
        return null;
    }
  });
}

// Small helper to avoid inline style creation in render loop
const PhaseContractorSuggested: React.FC<{ name: string }> = ({ name }) => {
  const classes = useColumnStyles();
  return (
    <label className={classes.lightgray} title="Suggested Contractor">
      {name}
    </label>
  );
};
